/**
 * PATH: apps/qa/src/pages/area-execution/ProgressBar.tsx
 * PURPOSE: Progress bar with pass/fail/na/pending stats for checklist execution
 */

import { useMemo } from 'react'
import type { ItemWithState } from './area-execution-types'
import { toUIStatus } from './area-execution-types'

export function ProgressBar({ items }: { items: ItemWithState[] }) {
  const stats = useMemo(() => {
    const total = items.length
    const done = items.filter(i => toUIStatus(i.status) === 'done').length
    const failed = items.filter(i => toUIStatus(i.status) === 'failed').length
    const na = items.filter(i => toUIStatus(i.status) === 'na').length
    return { total, done, failed, na, pending: total - done - failed - na }
  }, [items])

  const percentage = stats.total > 0 ? Math.round(((stats.done + stats.failed + stats.na) / stats.total) * 100) : 0

  return (
    <div className="bg-card rounded-xl p-4 border border-border shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-foreground">Progress</span>
        <span className="text-2xl font-bold text-primary">{percentage}%</span>
      </div>
      <div className="h-3 bg-muted rounded-full overflow-hidden flex">
        <div className="h-full bg-emerald-500 transition-all duration-300" style={{ width: `${(stats.done / Math.max(stats.total, 1)) * 100}%` }} />
        <div className="h-full bg-red-500 transition-all duration-300" style={{ width: `${(stats.failed / Math.max(stats.total, 1)) * 100}%` }} />
        <div className="h-full bg-slate-400 transition-all duration-300" style={{ width: `${(stats.na / Math.max(stats.total, 1)) * 100}%` }} />
      </div>
      <div className="grid grid-cols-4 gap-2 mt-3 text-center">
        <div><div className="text-lg font-bold text-emerald-600">{stats.done}</div><div className="text-xs text-muted-foreground">Passed</div></div>
        <div><div className="text-lg font-bold text-red-600">{stats.failed}</div><div className="text-xs text-muted-foreground">Failed</div></div>
        <div><div className="text-lg font-bold text-slate-500">{stats.na}</div><div className="text-xs text-muted-foreground">N/A</div></div>
        <div><div className="text-lg font-bold text-amber-600">{stats.pending}</div><div className="text-xs text-muted-foreground">Pending</div></div>
      </div>
    </div>
  )
}
